import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@/lib/auth-utils";
import { supabase } from "@/lib/supabase";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2, UserCheck } from "lucide-react";
import { AddNewTalentButton } from "@/features/common/AddNewTalentButton";
import { DeleteUserDialog } from "@/features/common/DeleteUserDialog";
import { useCallback, useEffect, useState } from "react";

interface User {
  id: string;
  email: string;
  given_name?: string;
  family_name?: string;
  partner_id?: string;
  company_id?: string;
  role: UserRole;
}

const getTableNameFromUserRole = (role: UserRole) => {
  if (role === "talent") {
    return "talent_profiles";
  } else if (role === "partner") {
    return "partner_profiles";
  } else {
    return "employer_profiles";
  }
};

function UserManagementContent() {
  const [users, setTalents] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    given_name: "",
    family_name: "",
    password: ""
  });
  const { toast } = useToast();
  const { impersonateUser } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      // Use admin function to get all users with emails for superuser administration
      const { data: profiles, error: profilesError } = await supabase.rpc(
        "get_all_users_admin_2"
      );
      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        throw profilesError;
      }

      // Map the data - the RPC function returns all needed fields including emails
      const users =
        profiles?.map((profile: any) => ({
          id: profile.id,
          given_name: profile.given_name || "",
          family_name: profile.family_name || "",
          email: profile.email || "",
          partner_id: profile.partiner_id || "",
          employer_id: profile.employer_id,
          role: profile.user_type
        })) || [];

      setTalents(users);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Fehler",
        description: "Benutzer konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      given_name: user.given_name || "",
      family_name: user.family_name || "",
      password: ""
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleImpersonate = async (user: User) => {
    try {
      await impersonateUser(user.id);
      toast({
        title: "Impersonierung gestartet",
        description: `Sie sind jetzt als ${user.given_name} ${user.family_name} angemeldet.`
      });
    } catch (error) {
      console.error("Error impersonating user:", error);
      toast({
        title: "Fehler",
        description: "Benutzer konnte nicht impersoniert werden.",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    try {
      if (!editingUser) return;
      const updateData = {
        given_name: formData.given_name,
        family_name: formData.family_name
      };

      const { error } = await supabase
        .from(getTableNameFromUserRole(editingUser.role))
        .update(updateData)
        .eq("id", editingUser.id);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Benutzer wurde aktualisiert."
      });

      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error saving user:", error);
      toast({
        title: "Fehler",
        description: "Benutzer konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    }
  };

  // Deletion handled inside DeleteUserDialog

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case "superuser":
        return "Superuser";
      case "admin":
        return "Administrator:in";
      case "staff":
        return "Mitarbeiter:in";
      case "partner":
        return "Partner";
      default:
        return role;
    }
  };

  const getRoleBadgeVariant = (role: UserRole) => {
    switch (role) {
      case "superuser":
        return "destructive";
      case "admin":
        return "default";
      case "staff":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => {
        const givenName = row.original.given_name || "";
        const familyName = row.original.family_name || "";
        const fullName = `${givenName} ${familyName}`.trim();
        return fullName || "-";
      }
    },
    {
      accessorKey: "email",
      header: "E-Mail"
    },
    {
      accessorKey: "role",
      header: "Rolle",
      cell: ({ row }) => {
        const role = row.getValue("role") as UserRole;
        return (
          <Badge variant={getRoleBadgeVariant(role)}>
            {getRoleLabel(role)}
          </Badge>
        );
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleImpersonate(row.original)}>
              <UserCheck className="mr-2 h-4 w-4" />
              Impersonieren
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openEditDialog(row.original)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => openDeleteDialog(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Löschen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  if (loading) {
    return <div>Lade Benutzer...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Benutzer verwalten</h2>
          <p className="text-gray-500">Verwalte alle Benutzer im System.</p>
        </div>
        <AddNewTalentButton isApplicationList={false} onCreated={fetchUsers} />
      </div>

      <DataTable
        columns={columns}
        data={users}
        searchKey="email"
        filterPlaceholder="Nach E-Mail suchen..."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>Bearbeite die Benutzerdaten.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">E-Mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="benutzer@beispiel.de"
                disabled
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="given_name">Vorname</Label>
                <Input
                  id="given_name"
                  value={formData.given_name}
                  onChange={(e) =>
                    setFormData({ ...formData, given_name: e.target.value })
                  }
                  placeholder="Max"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="family_name">Nachname</Label>
                <Input
                  id="family_name"
                  value={formData.family_name}
                  onChange={(e) =>
                    setFormData({ ...formData, family_name: e.target.value })
                  }
                  placeholder="Mustermann"
                />
              </div>
            </div>
            {/*
            // TODO: Bring back the option to change the user role via UI.
            <div className="grid gap-2">
              <Label htmlFor="role">Rolle *</Label>
              <Select
                value={formData.role}
                onValueChange={(value: UserRole) =>
                  setFormData({ ...formData, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Rolle wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Mitarbeiter</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="superuser">Superuser</SelectItem>
                </SelectContent>
              </Select>
            </div>*/}
            {/* Creation password field removed; creation handled by shared component */}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={!formData.email}>
              Aktualisieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        userId={userToDelete?.id || ""}
        userName={
          userToDelete
            ? `${userToDelete.given_name || ""} ${userToDelete.family_name || ""}`.trim()
            : ""
        }
        onDeleted={fetchUsers}
      />
    </div>
  );
}

export default function UserManagement() {
  return <UserManagementContent />;
}
